import {
  CANNOT_DELETE_ITEM_IN_USE_MSG,
  CloseButton,
  CrudMode,
  DELETE_WARNING,
  DropDownMenu,
  ListContext,
  ListContextValue,
  MenuItemType,
  Stack,
  Typography,
  UpdateButton,
  ValidationErrors,
  newToast,
} from "@compass/core";
import { DataModelInformation, SubmitButton } from "components";
import { DataModelEnum, Partner, Permission, PermissionAction } from "generated/openApi";
import { useContext } from "react";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router-dom";
import { setAppToast } from "redux/reducers/appSlice";
import { isAuthorized } from "services";
import ApiService from "services/apiService";

export const PartnerPageHeader = (props: {
  rootPermission: Permission;
  handle: string;
  crudMode: CrudMode;
  fetch: (id: number) => void;
  reroute: (id: number, crudMode: CrudMode) => void;
  partner: Partner;
  setValidationErrors: (errors: ValidationErrors) => void;
  unsavedChanges: boolean;
}) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const listContext = useContext<ListContextValue>(ListContext);
  const id = props.partner.id;

  const save = (partner: Partner) => {
    props.setValidationErrors({});

    if (id > 0) {
      ApiService.partners
        .saveUpdate(partner)
        .then(() => {
          props.fetch(id);
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
          props.reroute(id, "view");
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.data);
        });
    } else {
      ApiService.partners
        .saveCreate(partner)
        .then((response) => {
          const id = response;
          props.fetch(id);
          props.reroute(id, "view");
          listContext.loadList?.();
          dispatch(setAppToast(newToast("success")));
        })
        .catch((ex) => {
          dispatch(setAppToast(newToast("error")));
          props.setValidationErrors(ex.data);
        });
    }
  };

  const postDelete = (id: number) => {
    if (window.confirm(DELETE_WARNING)) {
      ApiService.partners.delete(id).then(() => {
        navigate("../");
        listContext.loadList?.();
        dispatch(setAppToast(newToast("success")));
      });
    }
  };

  const menuItems: MenuItemType[] = [];

  if (isAuthorized(props.rootPermission, PermissionAction.Delete))
    menuItems.push({
      label: <Typography color="error">Delete</Typography>,
      action: () => postDelete(id),
      disabled: props.partner.isInUse,
      tooltip: props.partner.isInUse ? CANNOT_DELETE_ITEM_IN_USE_MSG : undefined,
    });

  return (
    <Stack direction="row" justifyContent="space-between">
      <Stack direction="row" alignItems="center" spacing="sm">
        <Typography variant="inherit">{props.handle}</Typography>
        {menuItems.length > 0 && <DropDownMenu menuItems={menuItems} />}
      </Stack>

      <Stack direction="row" alignItems="center" spacing="sm">
        {<DataModelInformation dataModelType={DataModelEnum.Partner} dataModelId={id} />}

        <CloseButton confirm={props.unsavedChanges} />

        {props.crudMode === "view" && isAuthorized(props.rootPermission, PermissionAction.Update) && (
          <UpdateButton onClick={() => props.reroute(id, "edit")} />
        )}

        {props.crudMode === "edit" && (
          <SubmitButton onClick={() => save(props.partner)} disabled={!props.unsavedChanges} />
        )}
      </Stack>
    </Stack>
  );
};
